import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColorPicker } from "antd";
import { setToken } from "../../../config/index";
import changeStatus from "./api";
import { updateUserMeta } from "./api/index";
import EditProfile from "./EditProfile";
import { externalUsers, getCurrentUserRole } from "modules/utilities/utility";
import secureLocalStorage from "react-secure-storage";
import { toast } from "react-toastify";

/* ─── Theme base modes ─── */
const DARK_BASE = {
  rail: '#0f172a', panel: '#1e293b',
  text: '#e2e8f0', textMuted: '#94a3b8',
  border: 'rgba(255,255,255,0.06)', hoverBg: 'rgba(255,255,255,0.05)',
  letterBg: 'rgba(255,255,255,0.06)',
  brandFilter: 'brightness(1.5) contrast(1.15)',
};

const LIGHT_BASE = {
  rail: '#ffffff', panel: '#f8fafc',
  text: '#1e293b', textMuted: '#64748b',
  border: '#e2e8f0', hoverBg: 'rgba(0,0,0,0.04)',
  letterBg: 'rgba(0,0,0,0.06)',
  brandFilter: 'none',
};

const DEFAULT_ACCENT = '#3b82f6';

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [59, 130, 246];
}

/** Lighten a hex color by mixing with white */
function lightenHex(hex, amount) {
  const [r, g, b] = hexToRgb(hex);
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return `#${lr.toString(16).padStart(2,'0')}${lg.toString(16).padStart(2,'0')}${lb.toString(16).padStart(2,'0')}`;
}

/**
 * Apply theme to DOM via CSS custom properties.
 * themeConfig: { mode: "light"|"dark", accent: "#hex" }
 *   — legacy string values ("light"/"dark") still supported
 */
export function applyTheme(themeConfig) {
  let mode, accent;

  if (typeof themeConfig === 'string') {
    mode = themeConfig === 'dark' ? 'dark' : 'light';
    accent = DEFAULT_ACCENT;
  } else if (themeConfig && typeof themeConfig === 'object') {
    mode = themeConfig.mode || 'light';
    accent = themeConfig.accent || DEFAULT_ACCENT;
  } else {
    mode = 'light';
    accent = DEFAULT_ACCENT;
  }

  const base = mode === 'dark' ? DARK_BASE : LIGHT_BASE;
  const [r, g, b] = hexToRgb(accent);
  const gradientEnd = lightenHex(accent, 0.25);
  const root = document.documentElement;

  root.style.setProperty('--sidebar-rail-bg', base.rail);
  root.style.setProperty('--sidebar-panel-bg', base.panel);
  root.style.setProperty('--sidebar-accent', accent);
  root.style.setProperty('--sidebar-accent-15', `rgba(${r},${g},${b},0.15)`);
  root.style.setProperty('--sidebar-accent-20', `rgba(${r},${g},${b},0.2)`);
  root.style.setProperty('--avatar-gradient', `linear-gradient(135deg, ${accent}, ${gradientEnd})`);
  root.style.setProperty('--sidebar-text', base.text);
  root.style.setProperty('--sidebar-text-muted', base.textMuted);
  root.style.setProperty('--sidebar-border', base.border);
  root.style.setProperty('--sidebar-hover-bg', base.hoverBg);
  root.style.setProperty('--sidebar-letter-bg', base.letterBg);
  root.style.setProperty('--sidebar-brand-filter', base.brandFilter);
}

/**
 * Read stored theme from secureLocalStorage.
 * Returns { mode: string, accent: string }
 */
function getStoredTheme() {
  try {
    let raw = secureLocalStorage.getItem("theme");
    if (!raw) return { mode: 'light', accent: DEFAULT_ACCENT };
    // Parse JSON strings from secureLocalStorage
    if (typeof raw === 'string' && raw.startsWith('{')) {
      try { raw = JSON.parse(raw); } catch (_) {}
    }
    // Legacy simple string values
    if (typeof raw === 'string') {
      return { mode: raw === 'dark' ? 'dark' : 'light', accent: DEFAULT_ACCENT };
    }
    // Object format (new or legacy custom)
    if (typeof raw === 'object') {
      return {
        mode: raw.mode || 'light',
        accent: raw.accent || DEFAULT_ACCENT,
      };
    }
    return { mode: 'light', accent: DEFAULT_ACCENT };
  } catch {
    return { mode: 'light', accent: DEFAULT_ACCENT };
  }
}

/** Debounced backend sync — waits 1.5 s after last change before calling API */
let _metaSyncTimer = null;
function debouncedMetaSync(themeConfig) {
  if (_metaSyncTimer) clearTimeout(_metaSyncTimer);
  _metaSyncTimer = setTimeout(() => {
    updateUserMeta({ theme: themeConfig }).catch(() => {
      toast.error("Failed to save theme preference");
    });
  }, 1500);
}

/** Persist theme: localStorage instantly, API call debounced */
function persistTheme(themeConfig) {
  const storageValue = typeof themeConfig === 'object' ? JSON.stringify(themeConfig) : themeConfig;
  secureLocalStorage.setItem("theme", storageValue);
  debouncedMetaSync(themeConfig);
}

const ProfileDropdown = () => {
  const [status, setStatus] = useState("Online");
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme);
  const dropdownRef = useRef(null);

  const firstName = secureLocalStorage.getItem("first_name") || "";
  const email = secureLocalStorage.getItem("email") || "";
  const role = getCurrentUserRole() || "";
  const isExternal = externalUsers.includes(role);

  const initials = firstName
    .split(" ")
    .map((n) => n.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // Close dropdown on outside click (ignore Ant Design ColorPicker popover)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        // Ant Design ColorPicker renders its popup in a portal outside the dropdown.
        // Don't close if the click is inside the color picker popover.
        if (e.target.closest('.ant-color-picker')) return;
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = useCallback(() => {
    const columnVisibility = localStorage.getItem("ticketTableColumnVisibility");
    setToken("");
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    secureLocalStorage.clear();
    localStorage.clear();
    if (columnVisibility) {
      localStorage.setItem("ticketTableColumnVisibility", columnVisibility);
    }
    window.location.href = "/";
  }, []);

  const toggleStatus = useCallback(() => {
    const newStatus = status === "Online" ? "Offline" : "Online";
    setStatus(newStatus);
    changeStatus(newStatus);
  }, [status]);

  /** Update theme, apply to DOM, and persist */
  const updateTheme = useCallback((updates) => {
    setTheme((prev) => {
      const next = { ...prev, ...updates };
      applyTheme(next);
      persistTheme(next);
      return next;
    });
  }, []);

  const handleModeToggle = useCallback(() => {
    updateTheme({ mode: theme.mode === 'dark' ? 'light' : 'dark' });
  }, [theme.mode, updateTheme]);

  const handleAccentChange = useCallback((color) => {
    const hex = typeof color === 'string' ? color : color.toHexString();
    updateTheme({ accent: hex });
  }, [updateTheme]);

  const handleResetAccent = useCallback(() => {
    updateTheme({ accent: DEFAULT_ACCENT });
  }, [updateTheme]);

  const isDefaultAccent = theme.accent === DEFAULT_ACCENT;

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      {/* ── Trigger ── */}
      <button
        className="topbar__profile"
        onClick={() => setOpen(!open)}
        type="button"
      >
        <div className="topbar__avatar">
          {initials || <FontAwesomeIcon icon="user" />}
        </div>
        <span
          className={`topbar__status-dot topbar__status-dot--${status.toLowerCase()}`}
        />
        <div className="topbar__profile-info">
          <span className="topbar__profile-name">{firstName}</span>
          <span className="topbar__profile-role">{role.replace(/_/g, " ")}</span>
        </div>
        <FontAwesomeIcon icon="chevron-down" className="topbar__profile-chevron" />
      </button>

      {/* ── Dropdown ── */}
      {open && (
        <div className="topbar__dropdown">
          {/* User info header */}
          <div className="topbar__dropdown-header">
            <div className="topbar__dropdown-avatar">
              {initials || <FontAwesomeIcon icon="user" />}
            </div>
            <div className="topbar__dropdown-user-info">
              <span className="topbar__dropdown-user-name">{firstName}</span>
              <span className="topbar__dropdown-user-email">{email}</span>
            </div>
          </div>

          {/* Edit Profile */}
          {!isExternal && (
            <div className="topbar__dropdown-item">
              <span className="topbar__dropdown-item-icon">
                <FontAwesomeIcon icon="pencil-alt" />
              </span>
              <EditProfile />
            </div>
          )}

          {/* Status toggle */}
          {!isExternal && (
            <div className="topbar__dropdown-status" onClick={toggleStatus}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="topbar__dropdown-item-icon">
                  <FontAwesomeIcon icon="circle" />
                </span>
                Status
              </div>
              <span
                className={`topbar__dropdown-status-badge topbar__dropdown-status-badge--${status.toLowerCase()}`}
              >
                {status}
              </span>
            </div>
          )}

          {/* ── Theme section ── */}
          <div className="topbar__theme-section">
            {/* Mode toggle */}
            <div className="topbar__theme-row">
              <span className="topbar__theme-row-label">Mode</span>
              <button
                type="button"
                className={`topbar__mode-toggle ${theme.mode === 'dark' ? 'topbar__mode-toggle--dark' : ''}`}
                onClick={handleModeToggle}
                title={theme.mode === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
              >
                <span className="topbar__mode-toggle-thumb">
                  <FontAwesomeIcon icon={theme.mode === 'dark' ? 'moon' : 'sun'} />
                </span>
              </button>
            </div>

            {/* Accent color */}
            <div className="topbar__theme-row">
              <span className="topbar__theme-row-label">Accent</span>
              <div className="topbar__accent-controls">
                <button
                  type="button"
                  className={`topbar__accent-default${isDefaultAccent ? ' topbar__accent-default--active' : ''}`}
                  onClick={handleResetAccent}
                  title="Reset to default blue"
                >
                  Default
                </button>
                <ColorPicker
                  value={theme.accent}
                  onChange={handleAccentChange}
                  size="small"
                />
              </div>
            </div>
          </div>

          <div className="topbar__dropdown-divider" />

          {/* Logout */}
          <button
            className="topbar__dropdown-item topbar__dropdown-item--danger"
            onClick={handleLogout}
          >
            <span className="topbar__dropdown-item-icon">
              <FontAwesomeIcon icon="sign-out-alt" />
            </span>
            Log out
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
